import { datadogRum } from '@datadog/browser-rum';
import { Button, FeaturedIcon } from '@trm/ui-kit';
import { AlertCircle, ArrowLeft, RefreshCw01 } from '@trm/ui-kit/icons';
import { useEffect } from 'react';
import { isRouteErrorResponse, useNavigate, useRouteError } from 'react-router-dom';

/**
 * Shared route-level error boundary.
 *
 * This component is designed to be used as an `errorElement` in React Router's route
 * configuration. It catches and displays errors that occur during route rendering,
 * data loading, or data mutations for the route group it is attached to, and reports
 * the caught error to Datadog.
 *
 * @remarks
 * **Handled error types:**
 * - HTTP errors (via `isRouteErrorResponse`): 401, 403, 404, 500, and generic status codes
 * - JavaScript errors: connection failures, dynamic import failures, and general exceptions
 * - Unknown errors: non-Error objects, null, or undefined
 *
 * **Business rules:**
 * - 404 and 403 errors do NOT show a retry button (user action won't help)
 * - All other errors show both "Back" and "Try Again" buttons
 * - Stack traces are only displayed in development mode for debugging
 * - This boundary catches errors at the route level; component-level errors should use
 *   React's `ErrorBoundary` pattern instead
 *
 * @example
 * ```tsx
 * // In your route configuration
 * import RouteErrorBoundary from '~/components/RouteErrorBoundary';
 *
 * const dashboardRoutes = {
 *   path: '/',
 *   element: <DashboardLayout />,
 *   errorElement: <RouteErrorBoundary />,
 *   children: [
 *     { path: 'dashboard/default', element: <DashboardDefault /> },
 *   ],
 * };
 * ```
 */
export function RouteErrorBoundary() {
  const error = useRouteError();
  const navigate = useNavigate();

  useEffect(() => {
    const reported = error instanceof Error ? error : new Error(`Route error: ${String(error)}`);
    datadogRum.addError(reported, { errorType: 'route_error' });
  }, [error]);

  let errorMessage = 'Something went wrong';
  let errorDetails: string | undefined;
  let showRetry = true;

  if (isRouteErrorResponse(error)) {
    switch (error.status) {
      case 404:
        errorMessage = 'Page not found';
        errorDetails = "The page you're looking for doesn't exist or has been moved.";
        showRetry = false;
        break;
      case 401:
        errorMessage = 'Session expired';
        errorDetails = 'Your session may have expired. Please try refreshing or sign in again.';
        break;
      case 403:
        errorMessage = 'Access denied';
        errorDetails = "You don't have permission to access this page.";
        showRetry = false;
        break;
      case 500:
        errorMessage = 'Server error';
        errorDetails = 'We encountered a problem on our end. Please try again in a moment.';
        break;
      default:
        errorMessage = `Error ${error.status}`;
        errorDetails = error.statusText || 'An unexpected error occurred.';
    }
  } else if (error instanceof Error) {
    if (error.message.includes('Failed to fetch dynamically imported module')) {
      errorMessage = 'Loading error';
      errorDetails = 'The page failed to load. This usually resolves after a refresh.';
    } else if (error.message.includes('Failed to fetch')) {
      errorMessage = 'Connection error';
      errorDetails = 'Unable to connect to the server. Please check your internet connection.';
    } else {
      errorMessage = 'Application error';
      errorDetails =
        process.env.NODE_ENV === 'development'
          ? error.message
          : 'An unexpected error occurred. Please try again.';
    }
  } else {
    errorMessage = 'Unknown error';
    errorDetails =
      process.env.NODE_ENV === 'development'
        ? String(error)
        : 'An unexpected error occurred. Please try again.';
  }

  const handleGoBack = () => navigate(-1);

  const handleRetry = () => window.location.reload();

  return (
    <div className="flex w-full flex-1 items-center justify-center p-6">
      <div className="max-w-lg min-w-fit">
        <div className="mb-6 flex justify-center">
          <FeaturedIcon
            icon={AlertCircle}
            color="error"
            theme="light"
            size="xl"
            aria-hidden="true"
          />
        </div>

        <h1 className="mb-2 text-center text-xl font-semibold text-gray-900">{errorMessage}</h1>
        {errorDetails && <p className="mb-8 text-center text-sm text-gray-600">{errorDetails}</p>}

        <div className="flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Button color="secondary" size="md" iconLeading={ArrowLeft} onClick={handleGoBack}>
            Back
          </Button>

          {showRetry && (
            <Button color="primary" size="md" iconLeading={RefreshCw01} onClick={handleRetry}>
              Try Again
            </Button>
          )}
        </div>

        {process.env.NODE_ENV === 'development' && error instanceof Error && error.stack && (
          <div className="mt-8 rounded-lg border border-red-200 bg-red-50 p-4 text-left">
            <p className="mb-2 text-xs font-semibold text-red-700">Development Error Details:</p>
            <pre className="max-h-48 overflow-auto font-mono text-xs whitespace-pre-wrap text-red-600">
              {error.stack}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
}

export default RouteErrorBoundary;
